// Governance API — 화면 12 / 13 / 16.
// 권위: ARCH_DASHBOARD.md v1.8 §4.12 / §4.13 / §4.16 + §6.2 (REST 계약).

import { apiGet, apiPost, apiDelete } from './client';

// ── 공통 타입 ──────────────────────────────────────────
export type GovernanceStage = 'A' | 'B' | 'C' | 'D';
export const ALL_STAGES: GovernanceStage[] = ['A', 'B', 'C', 'D'];

// ── 화면 12: /api/v1/governance/current ─────────────────
export interface GovernanceCurrentOverrideFlag {
  active: boolean;
  triggered_at?: string | null;
  drop_value?: number | null;
  vix_jump?: number | null;
  rationale?: string | null;
}

export interface GovernanceCurrentLlmOverride {
  applied: boolean;
  rationale?: string | null;
  delta_score?: number | null;
  model?: string | null;
  cost_usd?: number | null;
  rollout_mode?: 'shadow' | 'canary' | 'full' | string | null;
}

export interface GovernanceCurrentDegraded {
  last_degraded_at?: string | null;
  last_failed_at?: string | null;
  degraded_7d_count?: number | null;
}

export interface GovernanceCurrent {
  trading_date: string;
  stage: GovernanceStage | string;
  total_score: number | null;
  scores: {
    trend: number | null;
    spread: number | null;
    breakout: number | null;
    damage: number | null;
  };
  override: {
    rapid_drop: GovernanceCurrentOverrideFlag;
    volatility_expansion: GovernanceCurrentOverrideFlag;
  };
  llm_override: GovernanceCurrentLlmOverride;
  max_invest_ratio: number;
  long_allowed: boolean;
  short_allowed: boolean;
  published_at: string;
  valid_until: string;
  source: 'redis' | 'db';
  redis_ttl_seconds?: number | null;
  degraded?: GovernanceCurrentDegraded | null;
  // 운영자 메타 (선택적)
  file_path?: string | null;
  file_mtime?: string | null;
  last_event_id?: string | null;
  last_trace_id?: string | null;
}

export async function getGovernanceCurrent(): Promise<GovernanceCurrent> {
  return apiGet<GovernanceCurrent>('/api/v1/governance/current');
}

// ── 화면 13: /api/v1/governance/sla-status ──────────────
export type SlaVerdict = 'SUCCESS' | 'DEGRADED' | 'LATE' | 'MISSING' | 'AGENT_DOWN';
export const ALL_SLA_VERDICTS: SlaVerdict[] = [
  'SUCCESS',
  'DEGRADED',
  'LATE',
  'MISSING',
  'AGENT_DOWN',
];

export interface SlaStatusItem {
  trading_date: string;
  verdict: SlaVerdict;
  published_at: string | null;
  offset_minutes: number | null;
  deadline_kst: string;
  event_id: string | null;
  trace_id: string | null;
  note: string | null;
}

export interface SlaKpi {
  success_rate_30d: number;
  late_count_30d: number;
  missing_count_30d: number;
  avg_offset_min: number;
}

export interface SlaStatusListResponse {
  items: SlaStatusItem[];
  total: number;
  page: number;
  size: number;
  kpi?: SlaKpi;
}

export interface SlaStatusQuery {
  from?: string;
  to?: string;
  verdict?: SlaVerdict[];
  page?: number;
  size?: number;
}

export async function listSlaStatus(q: SlaStatusQuery = {}): Promise<SlaStatusListResponse> {
  const p = new URLSearchParams();
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  if (q.verdict && q.verdict.length > 0) p.set('verdict', q.verdict.join(','));
  p.set('page', String(Math.max(0, q.page ?? 0)));
  p.set('size', String(Math.max(1, Math.min(200, q.size ?? 50))));
  return apiGet<SlaStatusListResponse>(`/api/v1/governance/sla-status?${p.toString()}`);
}

export async function getSlaSummary(windowDays = 30): Promise<SlaKpi> {
  return apiGet<SlaKpi>(
    `/api/v1/governance/sla-status/summary?window_days=${windowDays}`,
  );
}

// ── 화면 16: /api/v1/governance/stage-override ──────────
export interface StageOverrideCurrent {
  active: boolean;
  stage_override: GovernanceStage | null;
  ratio_override: number | null;
  set_by: string | null;
  reason: string | null;
  set_at: string | null;
  expires_at: string | null;
  current_context?: {
    trading_date: string;
    stage: GovernanceStage | string;
    max_invest_ratio: number;
  } | null;
}

export async function getStageOverride(): Promise<StageOverrideCurrent> {
  return apiGet<StageOverrideCurrent>('/api/v1/governance/stage-override');
}

export interface StageOverrideSetRequest {
  approver: string;
  reason: string;
  stage_override: GovernanceStage;
  ratio_override: number;
  password: string;
}

export interface StageOverrideSetResponse {
  audit_id: string;
  expires_at: string;
}

export async function setStageOverride(
  body: StageOverrideSetRequest,
): Promise<StageOverrideSetResponse> {
  return apiPost<StageOverrideSetResponse>('/api/v1/governance/stage-override', body);
}

export async function clearStageOverride(
  body: { approver: string; reason: string; password: string },
): Promise<void> {
  return apiDelete<void>('/api/v1/governance/stage-override', { data: body });
}

export type StageOverrideAction = 'SET' | 'CLEAR' | 'EXPIRED_AUTO';
export const ALL_OVERRIDE_ACTIONS: StageOverrideAction[] = ['SET', 'CLEAR', 'EXPIRED_AUTO'];

export interface StageOverrideAuditItem {
  audit_id: string;
  action: StageOverrideAction;
  action_at: string;
  set_by: string | null;
  stage_before: GovernanceStage | string | null;
  stage_after: GovernanceStage | string | null;
  ratio_before: number | null;
  ratio_after: number | null;
  reason: string | null;
  expires_at: string | null;
  before_payload?: Record<string, unknown> | null;
  after_payload?: Record<string, unknown> | null;
}

export interface StageOverrideAuditListResponse {
  items: StageOverrideAuditItem[];
  total: number;
  page: number;
  size: number;
}

export interface StageOverrideAuditQuery {
  from?: string;
  to?: string;
  action?: StageOverrideAction[];
  set_by?: string;
  page?: number;
  size?: number;
}

export async function listStageOverrideAudit(
  q: StageOverrideAuditQuery = {},
): Promise<StageOverrideAuditListResponse> {
  const p = new URLSearchParams();
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  if (q.action && q.action.length > 0) p.set('action', q.action.join(','));
  if (q.set_by) p.set('set_by', q.set_by);
  p.set('page', String(Math.max(0, q.page ?? 0)));
  p.set('size', String(Math.max(1, Math.min(200, q.size ?? 50))));
  return apiGet<StageOverrideAuditListResponse>(
    `/api/v1/governance/stage-override/audit?${p.toString()}`,
  );
}
